import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";
import { DateTime } from "luxon";

import type {
  AudioRecordingAudioBlob,
  AudioRecordingChannelData,
  AudioRecordingMetadataRecord,
  AudioRecordingMetadata,
  AudioRecordingSnapshot,
  AudioRecordingState,
  AudioRecordingTranscriptChunkRecord,
  AudioRecordingTranscriptSnapshotRecord,
} from "@/domains/dexie-db/models/audio-recording-legacy";
import { AudioRecordingLegacyMetadataRepository } from "./audio-recording-legacy-metadata-repository";
import { AudioRecordingLegacyBlobsRepository } from "./audio-recording-legacy-blobs-repository";
import { AudioRecordingLegacyTranscriptChunksRepository } from "./audio-recording-legacy-transcript-chunks-repository";
import { AudioRecordingLegacyTranscriptSnapshotsRepository } from "./audio-recording-legacy-transcript-snapshots-repository";
import { AudioRecordingLegacyCapturedAudioRepository } from "./audio-recording-legacy-captured-audio-repository";

const DEFAULT_CHANNEL = 0;

const sortTranscripts = (transcripts: TimestampedAssemblyAITurnMessage[]) =>
  [...transcripts].sort((a, b) => {
    if (a.turn_order === b.turn_order) {
      return (a.timestamp ?? 0) - (b.timestamp ?? 0);
    }
    return a.turn_order - b.turn_order;
  });

const sortSnapshots = (snapshots: AudioRecordingSnapshot[]) =>
  [...snapshots].sort((a, b) => a.snapshotTime - b.snapshotTime);

type SaveCapturedAudioIdArgs = {
  noteId?: string;
  recordingId: string;
  capturedAudioId: string;
  createdAt?: number;
};

type AppendTranscriptChunkArgs = {
  noteId?: string;
  recordingId: string;
  transcript: TimestampedAssemblyAITurnMessage;
  channel?: number;
  createdAt?: number;
};

type AppendTranscriptSnapshotArgs = {
  noteId?: string;
  recordingId: string;
  transcripts: TimestampedAssemblyAITurnMessage[];
  snapshotTime: number;
  channel?: number;
  createdAt?: number;
};

type AppendAudioBlobArgs = {
  noteId?: string;
  recordingId: string;
  blobData: Blob;
  createdAt?: number;
};

type GetRecordingStateArgs = {
  noteId: string;
  recordingId?: string;
};

type GetRecordingMetadataArgs = {
  recordingId: string;
};

type LoadRunRecordingArgs = {
  recordingId: string;
};

type ClearByNoteArgs = {
  noteId: string;
};

type ClearRecordingArgs = {
  recordingId: string;
};

type ListRecordingMetadataArgs = {
  limit?: number;
  order?: "asc" | "desc";
};

type AudioRecordingLegacyRepositoryDeps = {
  runs: AudioRecordingLegacyMetadataRepository;
  blobs: AudioRecordingLegacyBlobsRepository;
  transcriptChunks: AudioRecordingLegacyTranscriptChunksRepository;
  transcriptSnapshots: AudioRecordingLegacyTranscriptSnapshotsRepository;
  capturedAudio: AudioRecordingLegacyCapturedAudioRepository;
};

export class AudioRecordingLegacyRepository {
  private readonly runs: AudioRecordingLegacyMetadataRepository;
  private readonly blobs: AudioRecordingLegacyBlobsRepository;
  private readonly transcriptChunks: AudioRecordingLegacyTranscriptChunksRepository;
  private readonly transcriptSnapshots: AudioRecordingLegacyTranscriptSnapshotsRepository;
  private readonly capturedAudio: AudioRecordingLegacyCapturedAudioRepository;

  constructor({
    runs,
    blobs,
    transcriptChunks,
    transcriptSnapshots,
    capturedAudio,
  }: AudioRecordingLegacyRepositoryDeps) {
    this.runs = runs;
    this.blobs = blobs;
    this.transcriptChunks = transcriptChunks;
    this.transcriptSnapshots = transcriptSnapshots;
    this.capturedAudio = capturedAudio;
  }

  async clear(): Promise<void> {
    await Promise.all([
      this.runs.dexieTable.clear(),
      this.blobs.dexieTable.clear(),
      this.transcriptChunks.dexieTable.clear(),
      this.transcriptSnapshots.dexieTable.clear(),
      this.capturedAudio.dexieTable.clear(),
    ]);
  }

  async clearByNoteId({ noteId }: ClearByNoteArgs): Promise<void> {
    await Promise.all([
      this.runs.clearByNoteId(noteId),
      this.blobs.clearByNoteId(noteId),
      this.transcriptChunks.clearByNoteId(noteId),
      this.transcriptSnapshots.clearByNoteId(noteId),
      this.capturedAudio.clearByNoteId(noteId),
    ]);
  }

  async deleteByRecordingId({ recordingId }: ClearRecordingArgs): Promise<void> {
    await Promise.all([
      this.runs.deleteByRecordingId(recordingId),
      this.blobs.clearByRecordingId(recordingId),
      this.transcriptChunks.deleteByRecordingId(recordingId),
      this.transcriptSnapshots.deleteByRecordingId(recordingId),
      this.capturedAudio.deleteByRecordingId(recordingId),
    ]);
  }

  async hasAnyForNote({ noteId }: { noteId: string }): Promise<boolean> {
    const captured = await this.capturedAudio.getLatestByNoteId(noteId);
    if (captured) {
      const capturedRun = await this.runs.getByRecordingId(captured.recordingId);
      if (capturedRun?.softDeletedAt == null) {
        return true;
      }
    }

    const runs = await this.runs.list();
    return runs.some(run => run.noteId === noteId && run.softDeletedAt == null);
  }

  async saveCapturedAudioId({
    noteId,
    recordingId,
    capturedAudioId,
    createdAt = DateTime.now().toMillis(),
  }: SaveCapturedAudioIdArgs): Promise<void> {
    await this.capturedAudio.save({ noteId, recordingId, capturedAudioId, createdAt });
  }

  async saveRecordingMetadata({
    run,
    createdAt = DateTime.now().toMillis(),
  }: {
    run: AudioRecordingMetadata;
    createdAt?: number;
  }): Promise<void> {
    const next: AudioRecordingMetadataRecord = {
      ...run,
      lastUpdatedAt: createdAt ?? run.lastUpdatedAt ?? DateTime.now().toMillis(),
      softDeletedAt: run.softDeletedAt ?? null,
    };
    await this.runs.upsert(next);
  }

  async getRecordingMetadata({
    recordingId,
  }: GetRecordingMetadataArgs): Promise<AudioRecordingMetadata | null> {
    const run = await this.runs.getByRecordingId(recordingId);
    if (!run || run.softDeletedAt != null) {
      return null;
    }
    return run;
  }

  async listRecordingMetadata(
    args: ListRecordingMetadataArgs = {}
  ): Promise<AudioRecordingMetadata[]> {
    return this.runs.list(args);
  }

  async appendTranscriptChunk({
    noteId,
    recordingId,
    transcript,
    channel = DEFAULT_CHANNEL,
    createdAt = DateTime.now().toMillis(),
  }: AppendTranscriptChunkArgs): Promise<void> {
    await this.transcriptChunks.append({ recordingId, noteId, channel, transcript, createdAt });
  }

  async appendTranscriptSnapshot({
    noteId,
    recordingId,
    transcripts,
    snapshotTime,
    channel = DEFAULT_CHANNEL,
    createdAt = DateTime.now().toMillis(),
  }: AppendTranscriptSnapshotArgs): Promise<void> {
    await this.transcriptChunks.deleteByRecordingIdAndChannel(recordingId, channel);
    await this.transcriptSnapshots.insert({
      recordingId,
      noteId,
      channel,
      snapshotTime,
      transcripts: sortTranscripts(transcripts),
      createdAt,
    });
  }

  async appendAudioBlob({
    noteId,
    recordingId,
    blobData,
    createdAt = DateTime.now().toMillis(),
  }: AppendAudioBlobArgs): Promise<void> {
    const run = await this.runs.getByRecordingId(recordingId);
    const resolvedNoteId = noteId ?? run?.noteId;
    await this.blobs.append({ recordingId, noteId: resolvedNoteId, blobData, createdAt });
  }

  async getAudioBlobs({
    recordingId,
  }: {
    recordingId: string;
  }): Promise<AudioRecordingAudioBlob[]> {
    return this.blobs.listByRecordingId(recordingId);
  }

  async clearAudioBlobs({ recordingId }: { recordingId: string }): Promise<void> {
    await this.blobs.clearByRecordingId(recordingId);
  }

  async getRunRecordingState({
    recordingId,
  }: LoadRunRecordingArgs): Promise<AudioRecordingState | null> {
    const run = await this.runs.getByRecordingId(recordingId);
    if (!run || run.softDeletedAt != null) return null;
    return this.buildRecordingState(run.noteId ?? null, recordingId, run);
  }

  async getRecordingState({
    noteId,
    recordingId,
  }: GetRecordingStateArgs): Promise<AudioRecordingState | undefined> {
    let targetRecordingId = recordingId;
    let run: AudioRecordingMetadataRecord | undefined;

    if (targetRecordingId) {
      run = await this.runs.getByRecordingId(targetRecordingId);
    }

    if (!targetRecordingId) {
      const captured = await this.capturedAudio.getLatestByNoteId(noteId);
      targetRecordingId = captured?.recordingId;
      if (targetRecordingId) {
        run = await this.runs.getByRecordingId(targetRecordingId);
      }
    }

    if (!targetRecordingId) return undefined;
    if (run?.softDeletedAt != null) {
      return undefined;
    }

    return this.buildRecordingState(noteId, targetRecordingId, run ?? null);
  }

  async softDeleteByRecordingId(
    recordingId: string,
    timestamp = DateTime.now().toMillis()
  ): Promise<void> {
    await this.runs.softDeleteByRecordingId(recordingId, timestamp);
  }

  async softDeleteByNoteId(noteId: string, timestamp = DateTime.now().toMillis()): Promise<void> {
    await this.runs.softDeleteByNoteId(noteId, timestamp);
  }

  private async buildRecordingState(
    noteId: string | null,
    recordingId: string,
    run: AudioRecordingMetadataRecord | null
  ): Promise<AudioRecordingState> {
    const chunks = await this.transcriptChunks.listByRecordingId(recordingId);
    const snapshots = await this.transcriptSnapshots.listByRecordingId(recordingId);
    const audioBlobs = await this.blobs.listByRecordingId(recordingId);
    const captured = await this.capturedAudio.getByRecordingId(recordingId);

    const channels = this.buildChannels(chunks, snapshots);

    return {
      noteId: run?.noteId ?? captured?.noteId ?? noteId ?? undefined,
      recordingId,
      capturedAudioId: run?.capturedAudioId ?? captured?.capturedAudioId,
      channels,
      audioBlobs,
      metadata: run ?? undefined,
    };
  }

  private buildChannels(
    chunks: AudioRecordingTranscriptChunkRecord[],
    snapshots: AudioRecordingTranscriptSnapshotRecord[]
  ): AudioRecordingChannelData[] {
    const channelMap = new Map<
      number,
      { transcriptChunks: TimestampedAssemblyAITurnMessage[]; snapshots: AudioRecordingSnapshot[] }
    >();

    const ensureChannel = (channel: number) => {
      const existing = channelMap.get(channel);
      if (existing) return existing;
      const next = {
        transcriptChunks: [] as TimestampedAssemblyAITurnMessage[],
        snapshots: [] as AudioRecordingSnapshot[],
      };
      channelMap.set(channel, next);
      return next;
    };

    for (const chunk of chunks) {
      const entry = ensureChannel(chunk.channel ?? DEFAULT_CHANNEL);
      entry.transcriptChunks.push(chunk.transcript);
    }

    for (const snapshot of snapshots) {
      const entry = ensureChannel(snapshot.channel ?? DEFAULT_CHANNEL);
      entry.snapshots.push({
        snapshotTime: snapshot.snapshotTime,
        transcripts: sortTranscripts(snapshot.transcripts),
        createdAt: snapshot.createdAt,
      });
    }

    if (channelMap.has(1) && !channelMap.has(0)) {
      channelMap.set(0, { transcriptChunks: [], snapshots: [] });
    }

    return Array.from(channelMap.entries())
      .sort(([left], [right]) => left - right)
      .map(([channel, data]) => ({
        channel,
        transcriptChunks: sortTranscripts(data.transcriptChunks),
        snapshots: sortSnapshots(data.snapshots),
      }));
  }
}

export const createAudioRecordingLegacyRepository = (deps: AudioRecordingLegacyRepositoryDeps) =>
  new AudioRecordingLegacyRepository(deps);
